import type { IStorageProvider } from '@/interfaces/storage';

const key = 'project-opened:v1';
export interface ProjectOpenRecord {
  mainChipId: string;
  openedAt: number;
}
/** Optional local history, separate from circuit bytes and their save timestamps. */
export class ProjectOpenHistory {
  constructor(private readonly storage: IStorageProvider) {}

  async read(name: string): Promise<ProjectOpenRecord | undefined> {
    return (await this.readMany([name])).get(name);
  }

  async readMany(names: readonly string[]): Promise<Map<string, ProjectOpenRecord>> {
    const records = new Map<string, ProjectOpenRecord>();
    try {
      const stored =
        await this.storage.load<Record<string, Partial<ProjectOpenRecord>>>(key);
      for (const name of names) {
        const value = stored && Object.hasOwn(stored, name) ? stored[name] : undefined;
        if (
          value &&
          typeof value.mainChipId === 'string' &&
          value.mainChipId.length > 0 &&
          typeof value.openedAt === 'number' &&
          Number.isSafeInteger(value.openedAt) &&
          value.openedAt >= 0 &&
          value.openedAt <= 8_640_000_000_000_000
        )
          records.set(name, { mainChipId: value.mainChipId, openedAt: value.openedAt });
      }
    } catch {
      // Missing/corrupt/unavailable optional history is unknown, never guessed.
    }
    return records;
  }

  async record(name: string, mainChipId: string, openedAt: number): Promise<void> {
    try {
      await this.storage.update<Record<string, ProjectOpenRecord>>(key, (stored) => {
        const previous = stored && Object.hasOwn(stored, name) ? stored[name] : undefined;
        const validPrevious =
          previous?.mainChipId === mainChipId &&
          Number.isSafeInteger(previous.openedAt) &&
          previous.openedAt >= 0 &&
          previous.openedAt <= 8_640_000_000_000_000;
        return {
          ...stored,
          [name]: {
            mainChipId,
            openedAt: validPrevious ? Math.max(previous.openedAt, openedAt) : openedAt,
          },
        };
      });
    } catch {
      // A history failure must not turn a successful editor open into a failure.
    }
  }

  watch(onChange: () => void): () => void {
    return this.storage.subscribe?.(key, onChange) ?? (() => {});
  }

  async forget(name: string): Promise<void> {
    try {
      await this.storage.update<Record<string, ProjectOpenRecord>>(key, (stored) => {
        const records = { ...stored };
        delete records[name];
        return records;
      });
    } catch {
      // Circuit operations are independent of optional presentation metadata.
    }
  }
}
